import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { Icon } from '@kizen/kds/Icon';
import { useTranslation } from 'react-i18next';
import { Bar } from './Bar';
import { formatBillingCurrency } from '../../util';

const UNLIMITED_BAR_COLOR = 'bg-button-primary-hover';

/**
 * Every progress bar in Billing renders through StatisticRow, so the unlimited
 * rule lives here: full green once anything is used (like Total Custom
 * Objects), empty at zero. Limited rows keep the percentage they were given.
 */
const getBarProps = ({
  percentage,
  color,
  used,
  limit,
}: {
  percentage: number;
  color?: string;
  used: number;
  limit: number;
}) => {
  if (limit !== Infinity) {
    return { percentage, color };
  }

  if (used <= 0) {
    return { percentage: 0, color: UNLIMITED_BAR_COLOR };
  }

  return { percentage: 100, color: UNLIMITED_BAR_COLOR };
};

// The info icon is kept on the same line as the label's last word, so a
// wrapping label never leaves the icon alone on its own line.
const LabelWithInfo = ({ label, info }: { label: string; info: string }) => {
  const words = label.split(' ');
  const lastWord = words.pop();

  return (
    <>
      {words.length ? `${words.join(' ')} ` : null}
      <span className="whitespace-nowrap">
        {lastWord}
        <span className="ml-[5px] inline-flex align-middle">
          <Icon
            size="sm"
            icon="message-info-circle"
            tooltipEnabled
            tooltipTitleOverride=""
            tooltipDescriptionOverride={info}
            tooltipPosition="top"
          />
        </span>
      </span>
    </>
  );
};

type StatisticRowProps = {
  percentage: number;
  used: number;
  limit: number;
  label: string;
  info?: string;
  divisions?: {
    risk?: number;
    over?: number;
  };
  small?: boolean;
  emphasize?: boolean;
  color?: string;
  currencySymbol?: string;
  qaId?: string;
};

export const StatisticRow = (props: StatisticRowProps) => {
  const {
    percentage,
    used,
    limit,
    label,
    info,
    divisions,
    small,
    emphasize,
    color,
    currencySymbol,
    qaId,
  } = props;
  const { t } = useTranslation();

  const currencyMode = Boolean(currencySymbol);
  const size = small ? 'sm' : 'md';
  const weight = emphasize ? 'bold' : 'regular';

  const usedText = currencyMode
    ? formatBillingCurrency(used, true, currencySymbol)
    : used.toLocaleString();

  const limitText =
    limit === Infinity
      ? t('Unlimited')
      : currencyMode
        ? formatBillingCurrency(limit, true, currencySymbol)
        : limit.toLocaleString();

  return (
    <div data-qa-id={qaId}>
      <div className="flex justify-between gap-x-10">
        <Typography size={size} weight={weight} className="min-w-0">
          {info ? <LabelWithInfo label={label} info={info} /> : label}
        </Typography>
        <Typography size={size} weight={weight} className="whitespace-nowrap">
          {usedText} / {limitText}
        </Typography>
      </div>
      <Spacer size={10} />
      <div className="flex gap-x-5 w-full">
        <Bar
          {...getBarProps({
            percentage,
            color,
            // Compare in cents so a sub-cent amount that displays as $0 is empty.
            used: currencyMode ? Math.round(used * 100) : used,
            limit,
          })}
          divisions={divisions}
        />
      </div>
    </div>
  );
};
